import { useState, useEffect } from "react";
import { ArrowLeft, Pencil, Plus, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";
import GoalsService from "../../services/GoalsService";
import medicationService, { LocalMedication } from "../../services/MedicationService";
import { Skeleton } from "../../components/ui/skeleton";
import MedicationAddSheet from "../../components/MedicationAddSheet";

const GoalsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);
  const [medications, setMedications] = useState<LocalMedication[]>([]);
  const [initialMedications, setInitialMedications] = useState<LocalMedication[]>([]);
  const [medsLoading, setMedsLoading] = useState(true);
  const [medModal, setMedModal] = useState(false);
  const [addMedSheetOpen, setAddMedSheetOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [deletingMedId, setDeletingMedId] = useState<string | null>(null);
  const [medForm, setMedForm] = useState({ name: "", time: "", frequency: "" });

  const [formData, setFormData] = useState({
    weightLossGoal: "",
    mainReason: "",
    targetWeight: "",
    targetWeightUnit: "",
    goal: "",
    diabetesType: "",
    activityLevel: "",
    dietaryPreference: "",
    fastingPreference: "",
    fastingWindow: "",
  });

  // Map API activity level to UI display
  const mapActivityLevelFromAPI = (level: string): string => {
    const mapping: { [key: string]: string } = {
      sedentary: "Sedentary",
      lightly_active: "Lightly active",
      moderately_active: "Moderately active",
      very_active: "Very active",
      extremely_active: "Very active", // Map to closest option
    };
    return mapping[level] || "Moderately active";
  };

  // Map UI activity level to API value
  const mapActivityLevelToAPI = (level: string): string => {
    const mapping: { [key: string]: string } = {
      Sedentary: "sedentary",
      "Lightly active": "lightly_active",
      "Moderately active": "moderately_active",
      "Very active": "very_active",
    };
    return mapping[level] || "moderately_active";
  };

  // Map API diet type to UI display
  const mapDietTypeFromAPI = (diet: string): string => {
    const mapping: { [key: string]: string } = {
      classic: "Classic",
      vegetarian: "Vegetarian",
      vegan: "Vegan",
      pescatarian: "Pescatarian",
      keto: "Low Carb / Keto",
      low_carb: "Low Carb / Keto",
      paleo: "Classic", // Map to closest
      mediterranean: "Classic", // Map to closest
    };
    return mapping[diet] || "Classic";
  };

  // Map UI diet type to API value
  const mapDietTypeToAPI = (diet: string): string => {
    const mapping: { [key: string]: string } = {
      Classic: "classic",
      Vegetarian: "vegetarian",
      Vegan: "vegan",
      Pescatarian: "pescatarian",
      "Low Carb / Keto": "keto",
    };
    return mapping[diet] || "classic";
  };

  // Map API fasting window to UI display
  const mapFastingWindowFromAPI = (window: string): string => {
    const mapping: { [key: string]: string } = {
      // API format with underscores
      "16_8": "16:8 (Fast 16 hrs / Eat 8 hrs)",
      "18_6": "18:6 (Fast 18 hrs / Eat 6 hrs)",
      "14_10": "14:10 (Fast 14 hrs / Eat 10 hrs)",
      "20_4": "My eating window varies",
      // Also handle colon format if it comes
      "16:8": "16:8 (Fast 16 hrs / Eat 8 hrs)",
      "18:6": "18:6 (Fast 18 hrs / Eat 6 hrs)",
      "14:10": "14:10 (Fast 14 hrs / Eat 10 hrs)",
      "20:4": "My eating window varies",
      custom: "My eating window varies",
    };
    return mapping[window] || "16:8 (Fast 16 hrs / Eat 8 hrs)";
  };

  // Map UI fasting window to API value (API expects display format like "14:10")
  const mapFastingWindowToAPI = (window: string): string => {
    const mapping: { [key: string]: string } = {
      "16:8 (Fast 16 hrs / Eat 8 hrs)": "16:8",
      "18:6 (Fast 18 hrs / Eat 6 hrs)": "18:6",
      "14:10 (Fast 14 hrs / Eat 10 hrs)": "14:10",
      "My eating window varies": "custom",
    };
    return mapping[window] || "16:8";
  };

  // Map API intermittent fasting to UI display
  const mapIntermittentFastingFromAPI = (fasting: string): string => {
    const mapping: { [key: string]: string } = {
      yes_fasting_window: "Yes — I follow a fasting window",
      sometimes_not_sure: "Sometimes / Not sure",
      no_throughout_day: "No — I eat throughout the day",
      // Also handle full text versions if they come
      "Yes — I follow a fasting window": "Yes — I follow a fasting window",
      "Sometimes / Not sure": "Sometimes / Not sure",
      "No — I eat throughout the day": "No — I eat throughout the day",
    };
    return mapping[fasting] || "Yes — I follow a fasting window";
  };

  // Map UI intermittent fasting to API value (API expects display format)
  const mapIntermittentFastingToAPI = (fasting: string): string => {
    // API expects the display format, not snake_case
    return fasting || "Yes — I follow a fasting window";
  };

  // Map API weight loss reasons to UI display
  const mapWeightLossReasonsFromAPI = (reason: string): string => {
    const mapping: { [key: string]: string } = {
      health: "General health / feel better",
      diabetes: "To improve blood sugar / manage diabetes",
      doctor: "Doctor recommended it",
      confidence: "To look or feel more confident",
      appearance: "To look or feel more confident",
      medical: "Doctor recommended it",
      other: "Other",
    };
    return mapping[reason] || "General health / feel better";
  };

  // Map UI weight loss reasons to API value
  const mapWeightLossReasonsToAPI = (reason: string): string => {
    const mapping: { [key: string]: string } = {
      "General health / feel better": "health",
      "To improve blood sugar / manage diabetes": "diabetes",
      "Doctor recommended it": "doctor",
      "To look or feel more confident": "confidence",
      Other: "other",
    };
    return mapping[reason] || "health";
  };

  // Map API diabetes type to UI display
  const mapDiabetesTypeFromAPI = (type: string): string => {
    const mapping: { [key: string]: string } = {
      type1: "Type 1",
      type2: "Type 2",
      prediabetic: "Prediabetic",
      none: "None",
    };
    return mapping[type] || "None";
  };

  // Map UI diabetes type to API value
  const mapDiabetesTypeToAPI = (type: string): string => {
    const mapping: { [key: string]: string } = {
      "Type 1": "type1",
      "Type 2": "type2",
      Prediabetic: "prediabetic",
      None: "none",
    };
    return mapping[type] || "none";
  };

  // Load goals data from API
  useEffect(() => {
    const loadGoalsData = async () => {
      try {
        setLoading(true);

        // Load goals data
        const response = await GoalsService.getUserGoals();

        if (response && response.goals) {
          const goals = response.goals;

          // Use weight unit from goals API response instead of separate preferences call
          const weightUnit = goals.target_weight_in || "kg";

          // Get target weight based on preferred unit
          const targetWeight = GoalsService.getTargetWeight(goals, weightUnit);

          const loadedData = {
            weightLossGoal: goals.weight_loss_goal || "no",
            mainReason: mapWeightLossReasonsFromAPI(
              goals.weight_loss_reasons || "health"
            ),
            targetWeight: targetWeight ? Number(targetWeight).toFixed(1) : "",
            targetWeightUnit: weightUnit,
            goal:
              goals.weight_loss_goal === "yes"
                ? "Weight Loss"
                : "Maintain Weight",
            diabetesType: mapDiabetesTypeFromAPI(
              goals.diabetes_type || "none"
            ),
            activityLevel: mapActivityLevelFromAPI(
              goals.activity_level || "moderately_active"
            ),
            dietaryPreference: mapDietTypeFromAPI(goals.diet_type || "classic"),
            fastingPreference: mapIntermittentFastingFromAPI(
              goals.intermittent_fasting || "yes_fasting_window"
            ),
            fastingWindow: mapFastingWindowFromAPI(
              goals.fasting_window_type || "16_8"
            ),
          };

          setFormData(loadedData);
          setInitialData(loadedData);
        }
      } catch (error) {
        toast.error("Could not load goals. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadGoalsData();
  }, []);

  // Fetch medications from CRUD API
  const loadMedications = async () => {
    try {
      setMedsLoading(true);
      const meds = await medicationService.getAllMedications();
      setMedications(meds ?? []);
      setInitialMedications(meds ?? []);
    } catch {
      setMedications([]);
      setInitialMedications([]);
    } finally {
      setMedsLoading(false);
    }
  };

  useEffect(() => { loadMedications(); }, []);

  const handleMedicationAdded = async () => {
    const meds = await medicationService.getAllMedications();
    const list = meds ?? [];
    setMedications(list);
    setInitialMedications(list);
    toast.success("Medication added successfully!");
  };

  const openAddMedSheet = () => setAddMedSheetOpen(true);

  const openEditMed = (med: LocalMedication) => {
    setEditingMed(med);
    // Strip seconds from time (HH:MM:SS → HH:MM) for time input
    const timeWithoutSeconds = med.time ? med.time.substring(0, 5) : "";
    setMedForm({ name: med.medication_name, time: timeWithoutSeconds, frequency: med.frequency ?? "" });
    setMedModal(true);
  };

  const closeMedModal = () => {
    setMedModal(false);
    setEditingMed(null);
    setMedForm({ name: "", time: "", frequency: "" });
  };

  const handleMedSave = () => {
    if (!medForm.name.trim() || !medForm.time || !medForm.frequency.trim()) return;
    
    if (editingMed) {
      // Name is not editable in the modal; keep original medication_name from the record
      setMedications(prev => prev.map(m => 
        m.timing_id === editingMed.timing_id 
          ? { ...m, medication_name: editingMed.medication_name, time: medForm.time, frequency: medForm.frequency.trim() }
          : m
      ));
    }
    closeMedModal();
  };

  const handleMedDelete = async (timingId: string) => {
    if (deletingMedId) return;
    try {
      setDeletingMedId(timingId);
      await medicationService.deleteMedication(timingId);
      setMedications((prev) => prev.filter((m) => m.timing_id !== timingId));
      setInitialMedications((prev) =>
        prev.filter((m) => m.timing_id !== timingId),
      );
      window.dispatchEvent(new CustomEvent("medicationsUpdated"));
      toast.success("Medication deleted.");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete medication.";
      toast.error(msg);
    } finally {
      setDeletingMedId(null);
    }
  };

  const formatTime12 = (t: string) => {
    if (!t) return "-";
    const [hStr, mStr] = t.split(":");
    const h = parseInt(hStr, 10);
    const m = mStr ?? "00";
    const period = h >= 12 ? "PM" : "AM";
    return `${String(h % 12 || 12).padStart(2, "0")}:${m} ${period}`;
  };

  const handleBack = () => {
    const goalsChanged = JSON.stringify(formData) !== JSON.stringify(initialData);
    const medsChanged = JSON.stringify(medications) !== JSON.stringify(initialMedications);
    if (goalsChanged || medsChanged) {
      setLeaveConfirmOpen(true);
    } else {
      navigate("/glucera/profilepage");
    }
  };

  const handleSave = async () => {
    // Check if goals or medications have changed
    const goalsChanged = JSON.stringify(formData) !== JSON.stringify(initialData);
    const medsChanged = JSON.stringify(medications) !== JSON.stringify(initialMedications);
    
    if (!goalsChanged && !medsChanged) {
      toast.info("No changes made.");
      return;
    }


    // Prepare API payload with proper mapping (matching curl format)
    const targetWeightValue = parseFloat(formData.targetWeight) || 0;

    const goalsPayload = {
      weight_loss_goal: formData.weightLossGoal,
      weight_loss_reasons: mapWeightLossReasonsToAPI(formData.mainReason),
      target_weight_kg:
        formData.targetWeightUnit === "kg"
          ? targetWeightValue
          : targetWeightValue / 2.205,
      target_weight_lbs:
        formData.targetWeightUnit === "lbs"
          ? targetWeightValue
          : targetWeightValue * 2.205,
      target_weight_in: formData.targetWeightUnit,
      activity_level: mapActivityLevelToAPI(formData.activityLevel),
      diet_type: mapDietTypeToAPI(formData.dietaryPreference),
      diabetes_type: mapDiabetesTypeToAPI(formData.diabetesType),
      intermittent_fasting: mapIntermittentFastingToAPI(
        formData.fastingPreference
      ),
      fasting_window_type: mapFastingWindowToAPI(formData.fastingWindow),
    };

    try {
      setSaving(true);

      // Save goals if changed
      if (goalsChanged) {
        await GoalsService.updateUserGoals(goalsPayload);
        localStorage.setItem("userGoals", JSON.stringify(formData));
      }

      // Save medications if changed
      if (medsChanged) {
        // Find added, updated, deleted
        const initialIds = new Set(initialMedications.map(m => m.timing_id));
        const currentIds = new Set(medications.map(m => m.timing_id));

        // Delete removed medications
        for (const med of initialMedications) {
          if (!currentIds.has(med.timing_id)) {
            try {
              await medicationService.deleteMedication(med.timing_id);
            } catch (e) {
              console.error("Delete failed:", e);
            }
          }
        }

        // Update existing medications
        for (const med of medications) {
          const isModified = initialMedications.find(m => 
            m.timing_id === med.timing_id && 
            (m.medication_name !== med.medication_name || m.time !== med.time || m.frequency !== med.frequency)
          );

          if (isModified) {
            try {
              await medicationService.updateMedication(med.timing_id, {
                medication_name: med.medication_name,
                time: med.time,
                frequency: med.frequency,
              });
            } catch (e) {
              console.error("Update failed:", e);
            }
          }
        }
      }

      const successMessage =
        goalsChanged && medsChanged
          ? "Goals and medications updated successfully!"
          : goalsChanged
            ? "Goals updated successfully!"
            : "Medications updated successfully!";
      toast.success(successMessage);
      navigate("/glucera/profilepage");
    } catch (error) {
      toast.error(
        "Could not save changes. Please check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  // Goal is determined dynamically based on weight_loss_goal
  const activityLevels = [
    "Sedentary",
    "Lightly active",
    "Moderately active",
    "Very active",
  ];
  const dietTypes = [
    "Classic",
    "Vegetarian",
    "Vegan",
    "Pescatarian",
    "Low Carb / Keto",
  ];
  const fastingOptions = [
    "Yes — I follow a fasting window",
    "Sometimes / Not sure",
    "No — I eat throughout the day",
  ];
  const fastingWindows = [
    "16:8 (Fast 16 hrs / Eat 8 hrs)",
    "18:6 (Fast 18 hrs / Eat 6 hrs)",
    "14:10 (Fast 14 hrs / Eat 10 hrs)",
    "My eating window varies",
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {loading ? (
          <div className="space-y-4 pt-2">
            {/* Header skeleton */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="flex-1 space-y-1.5 text-center">
                <Skeleton className="mx-auto h-4 w-24 rounded-md" />
                <Skeleton className="mx-auto h-6 w-44 rounded-lg" />
              </div>
              <div className="h-10 w-10" />
            </div>
            {/* Cards skeleton */}
            <Skeleton className="h-28 w-full rounded-2xl" />
            <Skeleton className="h-44 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
            <Skeleton className="h-44 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
            <Skeleton className="h-36 w-full rounded-2xl" />
          </div>
        ) : (
          <>
            {/* Header: equal side slots so title centers without overlapping back */}
            <div className="flex items-center gap-2 pt-2">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="h-10 w-10 shrink-0 rounded-xl bg-card/80 hover:bg-card"
                  aria-label="Back to profile"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="min-w-0 flex-1 text-center">
                <p className="text-sm font-medium text-muted-foreground">My Account</p>
                <h1 className="text-2xl font-semibold text-foreground">Goals & Preferences</h1>
              </div>
              <div className="h-10 w-10 shrink-0" aria-hidden />
            </div>

            {/* Weight Loss Goal */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">
                Weight Loss Goal
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "yes" })
                  }
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-subhead font-medium transition-all ${formData.weightLossGoal === "yes"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                >
                  Yes
                </button>
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "no" })
                  }
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-subhead font-medium transition-all ${formData.weightLossGoal === "no"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                >
                  No
                </button>
                <button
                  onClick={() =>
                    setFormData({ ...formData, weightLossGoal: "not_sure" })
                  }
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-subhead font-medium transition-all ${formData.weightLossGoal === "not_sure"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                >
                  Not Sure
                </button>
              </div>
            </Card>

            {/* What's your main reason - Only show if Yes */}
            {formData.weightLossGoal === "yes" && (
              <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                <h3 className="mb-4 text-callout font-semibold text-foreground">
                  What's your main reason?
                </h3>
                <div className="space-y-2">
                  {[
                    "To improve blood sugar / manage diabetes",
                    "Doctor recommended it",
                    "General health / feel better",
                    "To look or feel more confident",
                    "Other",
                  ].map(reason => (
                    <button
                      key={reason}
                      onClick={() =>
                        setFormData({ ...formData, mainReason: reason })
                      }
                      className={`w-full rounded-lg border px-4 py-3 text-left text-subhead transition-all ${formData.mainReason === reason
                        ? "border-primary bg-primary/5 font-medium text-primary"
                        : "border-border text-foreground hover:border-primary/50"
                        }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </Card>
            )}

            {/* Target Weight - Only show if Yes */}
            {formData.weightLossGoal === "yes" && (
              <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                <h3 className="mb-4 text-callout font-semibold text-foreground">
                  Target Weight{" "}
                  <span className="text-muted-foreground">
                    ({formData.targetWeightUnit === "kg" ? "Kg" : "Lbs"})
                  </span>
                </h3>
                <div className="space-y-3">
                  <input
                    type="number"
                    placeholder="56"
                    value={formData.targetWeight}
                    onChange={e =>
                      setFormData({ ...formData, targetWeight: e.target.value })
                    }
                    className="w-full rounded-lg border border-border bg-card px-4 py-3 text-subhead focus:border-primary focus:outline-none"
                  />
                </div>
              </Card>
            )}

            {/* Diabetes Type */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-1 text-callout font-semibold text-foreground">
                Type Of Your Diabetes
              </h3>
              <p className="mb-4 text-subhead text-muted-foreground">
                Select your type of diabetes
              </p>
              <div className="space-y-2">
                {(["Type 1", "Type 2", "Prediabetic", "None"] as const).map(
                  option => (
                    <button
                      key={option}
                      onClick={() =>
                        setFormData({ ...formData, diabetesType: option })
                      }
                      className={`w-full rounded-xl border px-4 py-3 text-left text-subhead font-medium transition-all ${
                        formData.diabetesType === option
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-border text-foreground hover:border-primary/50"
                      }`}
                    >
                      {option}
                    </button>
                  )
                )}
              </div>
            </Card>

            {/* Activity Level */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">
                Activity Level
              </h3>
              <div className="space-y-2">
                {activityLevels.map(level => (
                  <button
                    key={level}
                    onClick={() =>
                      setFormData({ ...formData, activityLevel: level })
                    }
                    className={`w-full rounded-xl border p-3 text-left transition-all ${formData.activityLevel === level
                      ? "border-primary bg-primary/5 font-medium text-primary"
                      : "border-border text-foreground hover:border-primary/50"
                      }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </Card>

            {/* Diet Type */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">Diet Type</h3>
              <div className="space-y-2">
                {dietTypes.map(diet => (
                  <button
                    key={diet}
                    onClick={() =>
                      setFormData({ ...formData, dietaryPreference: diet })
                    }
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${formData.dietaryPreference === diet
                      ? "border-primary bg-primary/5 font-medium text-primary"
                      : "border-border text-foreground hover:border-primary/50"
                      }`}
                  >
                    {diet}
                  </button>
                ))}
              </div>
            </Card>

            {/* Fasting Preference */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <h3 className="mb-4 text-callout font-semibold text-foreground">
                Intermittent Fasting
              </h3>
              <div className="space-y-2">
                {fastingOptions.map(fasting => (
                  <button
                    key={fasting}
                    onClick={() =>
                      setFormData({ ...formData, fastingPreference: fasting })
                    }
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${formData.fastingPreference === fasting
                      ? "border-primary bg-primary/5 font-medium text-primary"
                      : "border-border text-foreground hover:border-primary/50"
                      }`}
                  >
                    {fasting}
                  </button>
                ))}
              </div>
            </Card>

            {/* Fasting Window - Only show if Yes */}
            {formData.fastingPreference ===
              "Yes — I follow a fasting window" && (
                <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
                  <h3 className="mb-4 text-callout font-semibold text-foreground">
                    Fasting Window
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {fastingWindows.map(window => (
                      <button
                        key={window}
                        onClick={() =>
                          setFormData({ ...formData, fastingWindow: window })
                        }
                        className={`rounded-lg border px-3 py-3 text-center text-subhead transition-all ${formData.fastingWindow === window
                          ? "border-primary bg-primary/5 font-medium text-primary"
                          : "border-border text-foreground hover:border-primary/50"
                          }`}
                      >
                        {window}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

            {/* Medications Section */}
            <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="text-callout font-semibold text-foreground">Medications</h3>
                <button
                  type="button"
                  onClick={openAddMedSheet}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 dark:text-primary dark:hover:bg-primary/10"
                >
                  <Plus className="h-4 w-4" strokeWidth={2.25} />
                  Add
                </button>
              </div>

              {/* Loading */}
              {medsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                </div>
              ) : medications.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-border py-6 text-center">
                  <p className="text-subhead text-muted-foreground">No medications added</p>
                  <button
                    type="button"
                    onClick={openAddMedSheet}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-gradient-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-primary transition-opacity hover:opacity-90"
                  >
                    <Plus className="h-4 w-4" strokeWidth={2.25} />
                    Add medication
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {medications.map((med) => (
                    <div
                      key={med.timing_id}
                      className="flex items-center justify-between rounded-lg border-2 border-border bg-background px-4 py-3"
                    >
                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-subhead font-medium capitalize text-foreground">
                          {med.medication_name}
                        </p>
                        <p className="mt-0.5 truncate text-caption1 text-muted-foreground">
                          {formatTime12(med.time)} • {med.frequency}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          onClick={() => openEditMed(med)}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleMedDelete(med.timing_id)}
                          disabled={deletingMedId === med.timing_id}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Save Button */}
            {(() => {
              const goalsChanged = JSON.stringify(formData) !== JSON.stringify(initialData);
              const medsChanged = JSON.stringify(medications) !== JSON.stringify(initialMedications);
              const hasChanges = goalsChanged || medsChanged;
              
              return (
                <>
                  {hasChanges && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-center dark:border-orange-800 dark:bg-orange-900/20">
                      <p className="text-caption1 font-semibold text-orange-700 dark:text-orange-400">
                        ⚠️ You have unsaved changes
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={handleSave}
                    disabled={saving || loading}
                    className="h-12 w-full rounded-2xl bg-gradient-primary text-primary-foreground shadow-primary hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : hasChanges ? "Save Changes" : "No Changes"}
                  </Button>
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* ── Medication Edit Modal ── */}
      {medModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-[400px] rounded-t-3xl bg-card p-6 shadow-2xl">
            {/* Header */}
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-callout font-bold text-foreground">
                Edit Medication
              </h2>
              <button
                onClick={closeMedModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-muted/80"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Medication Name */}
              <div>
                <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground uppercase tracking-wide">
                  Medication Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Dolo 650"
                  value={medForm.name}
                  onChange={e => setMedForm(p => ({ ...p, name: e.target.value }))}
                  disabled={!!editingMed}
                  className="w-full rounded-xl border-2 border-border bg-background px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground/50 focus:border-purple-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-muted/50 disabled:opacity-90"
                />
              </div>

              {/* Time */}
              <div>
                <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground uppercase tracking-wide">
                  Time
                </label>
                <input
                  type="time"
                  value={medForm.time}
                  onChange={e => setMedForm(p => ({ ...p, time: e.target.value }))}
                  className="w-full rounded-xl border-2 border-border bg-background px-4 py-3 text-subhead text-foreground focus:border-purple-500 focus:outline-none"
                />
              </div>

              {/* Frequency */}
              <div>
                <label className="mb-1.5 block text-caption1 font-semibold text-muted-foreground uppercase tracking-wide">
                  Frequency
                </label>
                <input
                  type="text"
                  placeholder="e.g., 500 mg · With breakfast"
                  value={medForm.frequency}
                  maxLength={100}
                  onChange={e =>
                    setMedForm(p => ({
                      ...p,
                      frequency: e.target.value.slice(0, 100),
                    }))
                  }
                  className="w-full rounded-xl border-2 border-border bg-background px-4 py-3 text-subhead text-foreground placeholder:text-muted-foreground/50 focus:border-purple-500 focus:outline-none"
                />
                <p className="mt-1 text-caption2 text-muted-foreground">
                  Dose or meal timing · max 100 characters
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={closeMedModal}
                className="flex-1 rounded-xl border-2 border-border py-3 text-subhead font-semibold text-muted-foreground transition-colors hover:bg-muted/50"
              >
                Cancel
              </button>
              <button
                onClick={handleMedSave}
                disabled={!medForm.name.trim() || !medForm.time || !medForm.frequency.trim()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-purple-600 py-3 text-subhead font-semibold text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      <MedicationAddSheet
        open={addMedSheetOpen}
        onClose={() => setAddMedSheetOpen(false)}
        onSaved={() => void handleMedicationAdded()}
      />

      {leaveConfirmOpen ? (
        <div className="fixed inset-0 z-[260] flex items-end justify-center bg-black/40 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-[400px] rounded-2xl border border-border bg-card p-5 shadow-2xl">
            <h3 className="text-base font-bold text-foreground">Unsaved changes</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You have unsaved changes. Leave without saving?
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setLeaveConfirmOpen(false)}
                className="h-11 rounded-xl border border-border bg-background text-sm font-semibold text-foreground hover:bg-muted/40"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={() => navigate("/glucera/profilepage")}
                className="h-11 rounded-xl bg-gradient-primary text-sm font-semibold text-primary-foreground shadow-primary hover:opacity-90"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GoalsPage;
